import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearch } from '@tanstack/react-router'
import { RefreshCw } from 'lucide-react'
import {
  Button,
  Chip,
  EmptyState,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  Select,
  toast,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
} from '@/components/ui'
import {
  useAssignItem,
  useResolveItem,
  useRunAssistant,
  useSnoozeItem,
  useTriageList,
  useTriggerRefresh,
} from '../api/queries'
import { useHasAnyPermission } from '@/shell/module-system'
import type { EmailType, InboxName, TriageItem } from '../api/types'
import { TriageActionCard } from '../components/TriageActionCard'
import { UserPickerModal } from '../components/UserPickerModal'
import {
  EMAIL_TYPES,
  INBOX_TABS,
  TYPE_LABEL,
  TYPE_VARIANT,
  compareTriageItems,
} from '../utils/triage-meta'

type InboxTab = InboxName | 'all'
type TypeFilter = EmailType | 'all'

type LookbackPreset = '24h' | '7d' | '14d' | 'all'

const LOOKBACK_OPTIONS: { value: LookbackPreset; label: string; hours: number | null }[] = [
  { value: '24h', label: 'Last 24h', hours: 24 },
  { value: '7d', label: 'Last 7 days', hours: 24 * 7 },
  { value: '14d', label: 'Last 14 days', hours: 24 * 14 },
  { value: 'all', label: 'All loaded', hours: null },
]

function lookbackToDateFrom(preset: LookbackPreset): string | undefined {
  const opt = LOOKBACK_OPTIONS.find((o) => o.value === preset)
  if (!opt || opt.hours == null) return undefined
  const cutoff = new Date(Date.now() - opt.hours * 60 * 60 * 1000)
  return cutoff.toISOString()
}

export function MaildeskTriagePage() {
  const [inbox, setInbox] = useState<InboxTab>('all')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')
  const [showSuppressed, setShowSuppressed] = useState(false)
  const [showResolved, setShowResolved] = useState(false)
  const [lookback, setLookback] = useState<LookbackPreset>('14d')
  const [pickerForId, setPickerForId] = useState<string | null>(null)

  // Memoize the cutoff so the query key is stable across renders.
  // lookbackToDateFrom calls `new Date()` — without useMemo it returns
  // a slightly different ISO string every render, which TanStack Query
  // treats as a new filter and refetches in a tight loop.
  const dateFrom = useMemo(() => lookbackToDateFrom(lookback), [lookback])

  const list = useTriageList({
    inbox: inbox === 'all' ? undefined : inbox,
    email_type: typeFilter === 'all' ? undefined : typeFilter,
    include_suppressed: showSuppressed,
    include_resolved: showResolved,
    date_from: dateFrom,
    limit: 200,
  })
  const refresh = useTriggerRefresh()
  const resolve = useResolveItem()
  const snooze = useSnoozeItem()
  const assign = useAssignItem()
  const runAssistant = useRunAssistant()
  const canRunAssistant = useHasAnyPermission([
    'admin',
    'sales',
    'operations', 'purchasing',
    'regulatory',
  ])

  const pickerContext = useMemo(() => {
    if (!pickerForId) return undefined
    const it = list.data?.items.find((i) => i.id === pickerForId)
    if (!it) return undefined
    return it.customer_name || it.sender_email || it.subject || undefined
  }, [pickerForId, list.data])

  // Banner "View Now" deep-link: scroll + highlight when ?itemId=... is
  // in the URL on landing.
  const search = useSearch({ strict: false }) as { itemId?: string }
  const [highlightedId, setHighlightedId] = useState<string | null>(null)
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const scrolledForId = useRef<string | null>(null)

  useEffect(() => {
    const target = search.itemId
    if (!target) return
    if (scrolledForId.current === target) return
    if (list.isLoading) return
    const el = cardRefs.current.get(target)
    if (!el) return
    scrolledForId.current = target
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setHighlightedId(target)
    // Fade the highlight after a few seconds; row stays visible.
    const timer = window.setTimeout(() => setHighlightedId(null), 3500)
    return () => window.clearTimeout(timer)
  }, [search.itemId, list.isLoading, list.data])

  const items = useMemo<TriageItem[]>(() => {
    const data = list.data?.items ?? []
    return [...data].sort(compareTriageItems)
  }, [list.data])

  const summary = list.data?.summary
  const totalOpen = summary?.total_open ?? 0
  const byType = summary?.by_type ?? {}
  const suppressedCount = summary?.suppressed ?? 0
  const crossInbox = summary?.cross_inbox ?? 0

  const handleOpenInMailDesk = (item: TriageItem) => {
    // Open the MailDesk client action (the real threaded UI), NOT
    // the maildesk.message_index form view. The MailDesk app exposes
    // a window action that takes a `message=` param to deep-link
    // straight into the right thread. action_id + menu_id are stable
    // for SPI's install; if they ever change, swap them here.
    if (item.latest_msg_index_id) {
      const url =
        'https://www.siliconesplus.com/web' +
        `#action=1552&message=${item.latest_msg_index_id}&cids=1&menu_id=972`
      window.open(url, '_blank', 'noopener,noreferrer')
      return
    }
    // Fallback: row not indexed yet — copy thread_id so operator can
    // paste-search MailDesk manually.
    if (navigator.clipboard && item.thread_id) {
      void navigator.clipboard.writeText(item.thread_id)
      toast.info('No MailDesk row id yet — thread id copied to clipboard.')
    }
  }

  const handleAssign = (id: string) => {
    setPickerForId(id)
  }

  const handlePickUser = (userId: number, userName: string) => {
    if (!pickerForId) return
    assign.mutate(
      { id: pickerForId, body: { assigned_to: userId } },
      {
        onSuccess: () => toast.success(`Assigned to ${userName}`),
        onError: (err) =>
          toast.danger('Assign failed', {
            description: err instanceof Error ? err.message : 'Unknown error',
          }),
      },
    )
    setPickerForId(null)
  }

  const busyId = resolve.isPending
    ? resolve.variables?.id
    : snooze.isPending
      ? snooze.variables?.id
      : undefined

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageHeaderBand
        title="Inbox Actions"
        description="Triaged + ranked email action items from MailDesk."
      >
        <MetricStrip variant="inline" className="ml-auto">
          <MetricCard variant="inline" label="Open" value={String(totalOpen)} />
          <MetricCard
            variant="inline"
            label="Cross-inbox"
            value={String(crossInbox)}
          />
          <MetricCard
            variant="inline"
            label="Suppressed"
            value={String(suppressedCount)}
          />
        </MetricStrip>
      </PageHeaderBand>

      {/* Tabs */}
      <Toolbar>
        <ToolbarLeft>
          <div role="tablist" className="flex items-center gap-1">
            {INBOX_TABS.map((t) => (
              <Button
                key={t.value}
                role="tab"
                aria-selected={inbox === t.value}
                variant={inbox === t.value ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setInbox(t.value)}
              >
                {t.label}
              </Button>
            ))}
          </div>
        </ToolbarLeft>
        <ToolbarRight>
          <Select
            value={lookback}
            onChange={(e) => setLookback(e.target.value as LookbackPreset)}
            aria-label="Date range"
          >
            {LOOKBACK_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowResolved((v) => !v)}
            aria-pressed={showResolved}
          >
            {showResolved ? 'Hide resolved' : 'Show resolved'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowSuppressed((v) => !v)}
            aria-pressed={showSuppressed}
          >
            {showSuppressed ? 'Hide suppressed' : `Show suppressed (${suppressedCount})`}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
          >
            <RefreshCw
              size={14}
              className={refresh.isPending ? 'animate-spin' : ''}
            />
            Refresh
          </Button>
        </ToolbarRight>
      </Toolbar>

      {/* Priority filter chips */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border-subtle bg-surface-sunk px-6 py-2">
        <Button
          variant={typeFilter === 'all' ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => setTypeFilter('all')}
        >
          All ({totalOpen})
        </Button>
        {EMAIL_TYPES.filter((t) => t !== 'C').map((t) => {
          const count = byType[t] ?? 0
          if (count === 0 && typeFilter !== t) return null
          return (
            <Button
              key={t}
              variant={typeFilter === t ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setTypeFilter(t)}
              aria-pressed={typeFilter === t}
            >
              <Chip size="xs" variant={TYPE_VARIANT[t]} dot>
                {TYPE_LABEL[t]}
              </Chip>
              <span className="text-role-badge-count ml-1.5">{count}</span>
            </Button>
          )
        })}
      </div>

      {/* Card list */}
      <div className="flex-1 overflow-y-auto bg-canvas px-6 py-4">
        {list.isLoading ? (
          <p className="text-role-metadata">Loading…</p>
        ) : list.isError ? (
          <EmptyState
            variant="error"
            title="Couldn't load triage items"
            description={
              list.error instanceof Error ? list.error.message : 'Unknown error'
            }
          />
        ) : items.length === 0 ? (
          <EmptyState
            title={
              showSuppressed
                ? 'No suppressed items in this filter.'
                : 'Inbox zero.'
            }
            description={
              showSuppressed
                ? 'Type C noise gets logged here for audit; nothing matched.'
                : 'No open action items match these filters. Refresh to recheck.'
            }
          />
        ) : (
          <div className="flex flex-col gap-3">
            {items.map((it) => (
              <div
                key={it.id}
                ref={(el) => {
                  if (el) cardRefs.current.set(it.id, el)
                  else cardRefs.current.delete(it.id)
                }}
                className={
                  highlightedId === it.id
                    ? 'rounded-md ring-2 ring-info-border transition-shadow duration-fast'
                    : ''
                }
              >
              <TriageActionCard
                item={it}
                busy={busyId === it.id}
                onResolve={(id) =>
                  resolve.mutate(
                    { id, body: {} },
                    {
                      onSuccess: () => toast.success('Resolved'),
                      onError: (err) =>
                        toast.danger('Resolve failed', {
                          description:
                            err instanceof Error ? err.message : 'Unknown error',
                        }),
                    },
                  )
                }
                onSnooze={(id, hours) =>
                  snooze.mutate(
                    { id, body: { snooze_hours: hours } },
                    {
                      onSuccess: () => toast.success(`Snoozed ${hours}h`),
                      onError: (err) =>
                        toast.danger('Snooze failed', {
                          description:
                            err instanceof Error ? err.message : 'Unknown error',
                        }),
                    },
                  )
                }
                onAssign={handleAssign}
                onOpenInMailDesk={() => handleOpenInMailDesk(it)}
                onRunAssistant={(id) =>
                  runAssistant.mutate(id, {
                    onSuccess: (data) =>
                      toast.success(
                        `Re-running CARLA (${data.agent_kind})…`,
                        { description: data.note ?? undefined },
                      ),
                    onError: (err) =>
                      toast.danger('Re-run failed', {
                        description:
                          err instanceof Error ? err.message : 'Unknown error',
                      }),
                  })
                }
                canRunAssistant={canRunAssistant}
              />
              </div>
            ))}
          </div>
        )}
      </div>

      <UserPickerModal
        open={pickerForId !== null}
        onOpenChange={(o) => {
          if (!o) setPickerForId(null)
        }}
        contextLabel={pickerContext}
        onPick={handlePickUser}
      />
    </div>
  )
}
